<script lang="ts">
	import BlurFade from '$lib/components/magic/blur-fade/blur-fade.svelte';
	import {
		Flame,
		CalendarDays,
		Target,
		UserPlus,
		Trophy,
		MousePointerClick,
		CircleUser,
		TrendingUp,
		Upload
	} from '@lucide/svelte';

	const tournamentTypes = [
		{
			icon: Flame,
			title: 'Always-on tournaments',
			desc: 'that keep fans engaged month after month',
			color: 'text-orange-500 bg-orange-50'
		},
		{
			icon: CalendarDays,
			title: 'Event-linked tournaments',
			desc: 'built around major moments such as international competitions, seasonal campaigns or partner projects',
			color: 'text-blue-500 bg-blue-50'
		},
		{
			icon: Target,
			title: 'Thematic tournaments',
			desc: 'built around a specific social or environmental theme like volunteering, health, water or energy',
			color: 'text-emerald-600 bg-emerald-50'
		},
		{
			icon: CircleUser,
			title: 'Do it yourself',
			desc: 'where users set up and run tournaments to their own specifications',
			color: 'text-purple-500 bg-purple-50'
		}
	];

	const engineSteps = [
		{ icon: UserPlus, label: 'Fans sign up' },
		{ icon: MousePointerClick, label: 'Choose who they represent' },
		{ icon: Target, label: 'Take actions' },
		{ icon: Upload, label: 'Upload proof' },
		{ icon: Trophy, label: 'Earn points' },
		{ icon: TrendingUp, label: 'Help their team move up the table' }
	];
</script>

<section class="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-12 items-center max-w-7xl mx-auto">
	<div class="lg:col-span-5 grid items-center space-y-8">
		<div
			class="inline-block px-4 py-1.5 bg-white shadow-pl rounded-full text-sm font-bold tracking-tight w-fit"
		>
			Our tournaments
		</div>
		<div class="font-title text-3xl md:text-4xl lg:text-5xl leading-tight">
			Proof <span class="text-planet-black3 font-light italic mr-1">not</span> promises
		</div>
		<div class="font-display font-bold text-lg leading-snug">
			<p>
				We run tournaments where fans compete for their clubs by
				<span class="text-planet-pink font-bold">doing good</span>.
			</p>
			<br />
			<p>
				<span class="text-planet-pink font-bold">Doing</span>, not just saying they'll do.
			</p>
		</div>

		<p class="font-display text-slate-600 text-base leading-relaxed">
			We design lightly competitive, fun campaigns where fans and their communities take verified
			social and environmental action.
		</p>
	</div>

	<div class="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
		{#each tournamentTypes as type (type.title)}
			<div
				class="group p-6 bg-white shadow-pl rounded-xl transition-all hover:translate-y-[-4px] duration-300 flex flex-col justify-between min-h-[200px]"
			>
				<div
					class={`relative w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${type.color}`}
				>
					<type.icon size={20} />
				</div>
				<div>
					<h3 class="text-lg font-bold mb-1 leading-tight">
						{type.title}
					</h3>
					<p class="text-sm text-slate-500 leading-snug">{type.desc}</p>
				</div>
			</div>
		{/each}
	</div>
</section>
<BlurFade delay={0.3} inView>
	<div class="mt-16 text-center">
		<p class="font-title text-center text-xl md:text-4xl text-wrap max-w-5xl mx-auto">
			Tournaments can be tailored with a choice of competitive formats, a wide range of activities
			and supporting local activation.
		</p>
	</div>
</BlurFade>
<BlurFade delay={0.4} inView>
	<section
		class="bg-white rounded-2xl shadow-pl p-8 md:p-16 text-planet-black relative overflow-hidden max-w-7xl mt-4"
	>
		<div
			class="bg-primary-gradient absolute bottom-0 right-0 h-96 w-96 -translate-y-1/2 translate-x-1/4 rounded-full opacity-20 blur-[120px]"
		></div>

		<div class="relative z-10">
			<h2 class="text-2xl md:text-4xl font-bold mb-8">
				All use the same <span class="bg-primary-gradient bg-clip-text text-transparent font-bold"
					>core engine</span
				>
			</h2>

			<div class="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-8">
				{#each engineSteps as step, index (step.label)}
					<div class="flex flex-col gap-4 group">
						<div class="flex items-center gap-3">
							<span class=" font-title text-planet-black">0{index + 1}</span>
							<div class="h-px grow bg-slate-800"></div>
						</div>
						<div class="flex items-center gap-4">
							<div class="p-3 bg-primary-gradient rounded-full text-white">
								<step.icon size={20} />
							</div>
							<span class="font-bold text-planet-black tracking-tight">
								{step.label}
							</span>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>
</BlurFade>
